import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dna, Gem, Newspaper, TextSearch, Plus, X } from 'lucide-react';
import type { BrandProfile, BrandProfileBranch, ContentMedium, PersonalWritingStyle } from '@/types';
import { CONTENT_CHANNELS, CONTENT_PURPOSES } from '@/constants/channels';
import { WritingStyleSelector } from "@/components/common/WritingStyleSelector";

interface BrandProfileDialogProps {
  brandProfiles: BrandProfile[];
  brandProfileBranches: BrandProfileBranch[];
  selectedBrandProfile: string;
  onSelect: (brandProfile: string) => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function BrandProfileDialog({
  brandProfiles,
  brandProfileBranches,
  selectedBrandProfile,
  onSelect,
  children,
  open: externalOpen,
  onOpenChange,
}: BrandProfileDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selected, setSelected] = useState(selectedBrandProfile);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  React.useEffect(() => {
    setSelected(selectedBrandProfile);
  }, [selectedBrandProfile]);

  const handleSave = () => {
    onSelect(selected);
    setOpen(false);
  };

  const handleRemove = () => {
    onSelect('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dna className="h-5 w-5" />
            Select Brand Profile
          </DialogTitle>
          <DialogDescription>
            Choose a brand profile to align your content with your brand's tone and voice.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Brand Profiles</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger>
                <SelectValue placeholder="Select a brand profile" />
              </SelectTrigger>
              <SelectContent>
                {brandProfiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </SelectItem>
                ))}
                {brandProfileBranches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name} (Branch)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {selectedBrandProfile && (
            <Button variant="destructive" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface WritingStyleDialogProps {
  selectedStyleId: string;
  selectedStyleType: 'brand' | 'personal' | null;
  onSelect: (styleId: string, styleType: 'brand' | 'personal') => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function WritingStyleDialog({
  selectedStyleId,
  selectedStyleType,
  onSelect,
  children,
  open: externalOpen,
  onOpenChange,
}: WritingStyleDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<string>('');

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  React.useEffect(() => {
    // Construct the value format expected by WritingStyleSelector
    if (selectedStyleType === 'brand' && selectedStyleId) {
      setSelectedProfile(`brand:${selectedStyleId}`);
    } else if (selectedStyleType === 'personal' && selectedStyleId) {
      setSelectedProfile(`style:${selectedStyleId}`);
    } else {
      setSelectedProfile('');
    }
  }, [selectedStyleId, selectedStyleType]);

  const handleProfileChange = (value: string, type: 'brand' | 'personal' | null, id: string) => {
    setSelectedProfile(value);
    if (type && id) {
      onSelect(id, type);
      setOpen(false);
    }
  };

  const handleRemove = () => {
    onSelect('', 'brand');
    setSelectedProfile('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dna className="h-5 w-5" />
            Select Writing Style
          </DialogTitle>
          <DialogDescription>
            Choose a writing style to guide your content's tone and voice.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <WritingStyleSelector
            value={selectedProfile}
            onValueChange={handleProfileChange}
            placeholder="Select a writing style or brand..."
          />
        </div>
        <DialogFooter className="flex flex-row gap-2 justify-end">
          {selectedStyleId && (
            <Button variant="destructive" className="mr-auto" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>

        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PurposeDialogProps {
  purpose: string;
  onSave: (purpose: string, setAsDefault?: boolean, shouldClearDefault?: boolean) => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedStyleId?: string;
  selectedStyleType?: 'brand' | 'personal' | null;
  defaultPurpose?: string | null;
}

export function PurposeDialog({
  purpose,
  onSave,
  children,
  open: externalOpen,
  onOpenChange,
  selectedStyleId,
  selectedStyleType,
  defaultPurpose,
}: PurposeDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [value, setValue] = useState(purpose);
  const [setAsDefault, setSetAsDefault] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  React.useEffect(() => {
    setValue(purpose);
    // Only set as default if the current purpose matches the default purpose
    setSetAsDefault(Boolean(purpose && defaultPurpose && purpose.trim() === defaultPurpose.trim()));
  }, [purpose, defaultPurpose]);

  // When user changes the text, automatically update toggle based on whether it matches default
  React.useEffect(() => {
    if (defaultPurpose) {
      const matches = value.trim() === defaultPurpose.trim();
      setSetAsDefault(matches);
    }
  }, [value, defaultPurpose]);

  const handleSave = () => {
    // When untoggling, only clear default if current value matches existing default
    // When toggling ON, always set the current value as the new default
    const shouldClearDefault = Boolean(!setAsDefault && defaultPurpose && value.trim() === defaultPurpose.trim());
    const shouldSetDefault = setAsDefault;

    onSave(value, shouldSetDefault, shouldClearDefault);
    setOpen(false);
  };

  const handleRemove = () => {
    onSave('');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gem className="h-5 w-5" />
            Content Purpose
          </DialogTitle>
          <DialogDescription>
            Describe the purpose and goal of this content to get better AI feedback.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* Quick purpose suggestions */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Quick Select</label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_PURPOSES.map((purpose) => (
                <Badge
                  key={purpose.value}
                  variant={value.toLowerCase().includes(purpose.value) ? "default" : "outline"}
                  className="cursor-pointer hover:bg-primary/10"
                  onClick={() => setValue(purpose.label)}
                >
                  {purpose.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Purpose/Goal</label>
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g., Generate leads for our SaaS product, educate customers about our new feature, increase brand awareness..."
              className="min-h-[100px]"
            />
          </div>
          {selectedStyleId && selectedStyleType && (
            <div className="flex items-center justify-between pt-2">
              <label className="text-sm font-medium">
                Use as default
              </label>
              <Switch
                checked={setAsDefault}
                onCheckedChange={setSetAsDefault}
              />
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-row gap-2 justify-end">
          {purpose && (
            <Button variant="destructive" className="mr-auto" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>

          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ChannelDialogProps {
  channel: string;
  onSave: (channel: string) => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChannelDialog({ channel, onSave, children, open: externalOpen, onOpenChange }: ChannelDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedMedium, setSelectedMedium] = useState<ContentMedium | 'custom'>('seo_article');
  const [customChannel, setCustomChannel] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  React.useEffect(() => {
    if (channel) {
      // Check if channel matches any value in CONTENT_CHANNELS (excluding 'custom')
      const validMediumValues = CONTENT_CHANNELS
        .filter(ch => ch.value !== 'custom')
        .map(ch => ch.value);

      if (validMediumValues.includes(channel)) {
        setSelectedMedium(channel as ContentMedium);
        setIsCustom(false);
      } else {
        setSelectedMedium('custom');
        setCustomChannel(channel);
        setIsCustom(true);
      }
    }
  }, [channel]);

  const handleSave = () => {
    const value = isCustom ? customChannel : selectedMedium;
    onSave(value);
    setOpen(false);
  };

  const handleRemove = () => {
    onSave('');
    setOpen(false);
  };

  const mediumLabels: Record<string, string> = Object.fromEntries(
    CONTENT_CHANNELS.map(ch => [ch.value, ch.label])
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Content Channel
          </DialogTitle>
          <DialogDescription>
            Select the channel or medium where this content will be published.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Channel/Medium</label>
            <Select
              value={isCustom ? 'custom' : selectedMedium}
              onValueChange={(value) => {
                if (value === 'custom') {
                  setIsCustom(true);
                  setSelectedMedium('custom');
                } else {
                  setIsCustom(false);
                  setSelectedMedium(value as ContentMedium);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a channel" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(mediumLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isCustom && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Custom Channel</label>
              <Input
                value={customChannel}
                onChange={(e) => setCustomChannel(e.target.value)}
                placeholder="Enter custom channel name"
              />
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-row gap-2 justify-end">
          {channel && (
            <Button variant="destructive" className="mr-auto" onClick={handleRemove}>
              Remove
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>

          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SeoKeywordsDialogProps {
  keywords: string[];
  onSave: (keywords: string[]) => void;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SeoKeywordsDialog({ keywords, onSave, children, open: externalOpen, onOpenChange }: SeoKeywordsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [keywordList, setKeywordList] = useState<string[]>(keywords);
  const [newKeyword, setNewKeyword] = useState('');

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (newOpen: boolean) => {
    if (externalOpen !== undefined && onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  React.useEffect(() => {
    setKeywordList(keywords);
  }, [keywords]);

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !keywordList.includes(newKeyword.trim())) {
      setKeywordList([...keywordList, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywordList(keywordList.filter((k) => k !== keyword));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSave = () => {
    onSave(keywordList);
    setOpen(false);
  };

  const handleRemoveAll = () => {
    onSave([]);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-[90vw] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TextSearch className="h-5 w-5" />
            SEO Keywords
          </DialogTitle>
          <DialogDescription>
            Add keywords you want to target with this content for better SEO optimization.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Keywords</label>
            <div className="flex gap-2">
              <Input
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter a keyword"
                className="flex-1"
              />
              <Button onClick={handleAddKeyword} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {keywordList.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Added Keywords</label>
              <div className="flex flex-wrap gap-2">
                {keywordList.map((keyword, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {keyword}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-row gap-2 justify-end">
          {keywords.length > 0 && (
            <Button variant="destructive" className="mr-auto" onClick={handleRemoveAll}>
              Remove All
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>

          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
